import React, { useEffect, useMemo } from 'react';
import { Alert, Typography, Button, Box } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Add as AddIcon, Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLocations } from '../hooks/useLocations';
import { useVRPData } from '../contexts/VRPDataContext';
import { useLocationsState } from '../hooks/useLocationsState';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';

// Components
import { BulkActionsToolbar } from '../components/locations/BulkActionsToolbar';
import { LocationsTable } from '../components/locations/LocationsTable';
import { EmptyState } from '../components/locations/EmptyState';
import { LoadingState } from '../components/locations/LoadingState';
import { ViewLocationDialog } from '../components/locations/ViewLocationDialog';
import { LocationForm } from '../components/locations/LocationForm';
import { BulkImportDialog } from '../components/bulk/BulkImportDialog';
import BulkExportDialog from '../components/bulk/BulkExportDialog';

export const Locations: React.FC = () => {
  const navigate = useNavigate();
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const { currentDataset } = useVRPData();
  
  const { state, handlers } = useLocationsState(currentProject);
  
  const { 
    data: locations, 
    isLoading, 
    error 
  } = useLocations(currentProject?.id || 0, currentScenario?.id, {
    limit: 100,
    dataset_id: currentDataset?.id
  });

  // Safe locations array - computed inline to avoid hook dependency cycles
  const getSafeLocations = () => Array.isArray(locations) ? locations : [];
  // Debug logging for project context
  useEffect(() => {
    console.log('📍 [Locations] Context state:', {
      currentProject: currentProject?.name || 'none',
      currentScenario: currentScenario?.name || 'none',
      currentDataset: currentDataset?.name || 'none',
      projectsAvailable: projects?.length || 0,
      localStorage: localStorage.getItem('vrp-current-project-id')
    });

    // If no current project but we have a saved project ID, try to restore it using unified navigation
    if (!currentProject && projects.length > 0) {
      const savedProjectId = localStorage.getItem('vrp-current-project-id');
      if (savedProjectId) {
        const savedProject = projects.find(p => p.id === parseInt(savedProjectId));
        if (savedProject) {
          console.log('🔄 [Locations] Restoring project from localStorage via unified navigation:', savedProject.name);
          actions.navigateToProject(savedProject.id);
        }
      }
    }
  }, [currentProject, currentScenario, currentDataset, projects, actions]);

  // Debug locations data - simplified dependencies
  useEffect(() => {
    const locationsArray = Array.isArray(locations) ? locations : [];
    console.log('📍 [Locations] Locations data updated:', {
      raw: locations,
      safe: locationsArray,
      count: locationsArray.length,
      hasContext: !!(currentScenario && currentDataset),
      queryContext: {
        projectId: currentProject?.id,
        scenarioId: currentScenario?.id,
        datasetId: currentDataset?.id
      },
      locations: locationsArray.map(l => ({ id: l.id, name: l.name }))
    });
  }, [locations, currentProject?.id, currentScenario?.id, currentDataset?.id]);

  // Selection handlers - simplified to avoid circular dependencies
  const handleSelectAllRows = (checked: boolean) => {
    const locationsArray = Array.isArray(locations) ? locations : [];
    handlers.selectAllRows(locationsArray, checked);
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  // Show project selection message if no current project
  if (!currentProject) {
    return (
      <ProfessionalLayout
        pageTitle="Locations"
        pageSubtitle="Please select a project to view locations"
        activeTab="Locations"
        actionButtons={
          <Button 
            variant="contained"
            onClick={() => navigate('/projects')}
            startIcon={<ArrowBackIcon />}
          >
            Select Project
          </Button>
        }
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            No Project Selected
          </Typography>
          <Typography variant="body1" gutterBottom>
            Please select a project from the dashboard to view its locations.
          </Typography>
          {projects.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Available projects: {projects.map(p => p.name).join(', ')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleBackToDashboard}
              startIcon={<ArrowBackIcon />}
            >
              Back to Dashboard
            </Button>
            {projects.length > 0 && (
              <Button 
                variant="contained"
                onClick={() => actions.navigateToProject(projects[0].id)}
              >
                Select First Project
              </Button>
            )}
          </Box>
        </Alert>
      </ProfessionalLayout>
    );
  }
  // Loading state
  if (isLoading) {
    return (
      <ProfessionalLayout
        pageTitle="Locations"
        pageSubtitle={`Loading locations for ${currentProject.name}...`}
        activeTab="Locations"
      >
        <LoadingState projectName={currentProject.name} type="skeleton" />
      </ProfessionalLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <ProfessionalLayout
        pageTitle="Locations"
        pageSubtitle="Error loading locations"
        activeTab="Locations"
        actionButtons={
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        }
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Error Loading Locations
          </Typography>
          <Typography variant="body1">
            {error instanceof Error ? error.message : 'Unknown error occurred'}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleBackToDashboard}
              sx={{ mr: 2 }}
              startIcon={<ArrowBackIcon />}
            >
              Back to Dashboard
            </Button>
            <Button 
              variant="contained" 
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Box>
        </Alert>
      </ProfessionalLayout>
    );
  }

  // Create enhanced subtitle with context information - inline function to avoid useMemo issues
  const getPageSubtitle = () => {
    if (!currentProject?.name) return 'Manage locations';
    
    let subtitle = `Manage locations for ${currentProject.name}`;
    if (currentScenario?.name) {
      subtitle += ` • Scenario: ${currentScenario.name}`;
    }
    if (currentDataset?.name) {
      subtitle += ` • Dataset: ${currentDataset.name}`;
    }
    return subtitle;
  };

  // Main content
  return (
    <ProfessionalLayout
      pageTitle="Locations"
      pageSubtitle={getPageSubtitle()}
      activeTab="Locations"
      actionButtons={
        <>
          <Button 
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handlers.openBulkImport}
            sx={{ mr: 1 }}
          >
            Import
          </Button>
          {getSafeLocations().length > 0 && (
            <Button 
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handlers.openBulkExport}
              sx={{ mr: 1 }}
            >
              Export
            </Button>
          )}
          <Button 
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handlers.openForm}
          >
            New Location
          </Button>
        </>
      }
    >
      {/* Context Warning Alert */}
      {(!currentScenario || !currentDataset) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Incomplete Context</Typography>
          <Typography variant="body2">
            {!currentScenario && !currentDataset 
              ? 'Please select a scenario and dataset to view location data.'
              : !currentScenario 
              ? 'Please select a scenario to proceed.'
              : 'Please select a dataset to load location data.'}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Navigate to: Project → Scenarios → Data Management → Locations
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/scenarios')}
              size="small"
              sx={{ mr: 1 }}
            >
              Go to Scenarios
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/data-management')}
              size="small"
            >
              Go to Data Management
            </Button>
          </Box>
        </Alert>
      )}

      {getSafeLocations().length > 0 ? (
        <Box>
          <BulkActionsToolbar
            selectedCount={state.selectedRows.length}
            bulkAction={state.bulkAction}
            onBulkActionChange={handlers.setBulkAction}
            onApplyBulkAction={handlers.applyBulkAction}
            onClearSelection={handlers.clearSelection}
          />

          <LocationsTable
            locations={getSafeLocations()}
            selectedRows={state.selectedRows}
            onSelectRow={handlers.selectRow}
            onSelectAll={handleSelectAllRows}
            onView={handlers.viewLocation}
            onEdit={handlers.editLocation}
            onDelete={handlers.deleteLocation}
          />
        </Box>
      ) : (
        <EmptyState
          projectName={currentProject.name}
          onAddFirst={handlers.openForm}
        />
      )}
      
      {/* Context Information */}
      {currentScenario && currentDataset && getSafeLocations().length === 0 && !isLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            No locations found in <strong>{currentDataset.name}</strong> for scenario <strong>{currentScenario.name}</strong>.
            You can add locations using the "New Location" button or import them from a CSV file.
          </Typography>
        </Alert>
      )}
      {/* Dialogs */}
      <ViewLocationDialog
        open={state.isViewDialogOpen}
        location={state.viewingLocation}
        onClose={handlers.closeViewDialog}
        onEdit={handlers.editLocation}
      />

      <LocationForm
        open={state.isFormOpen}
        onClose={handlers.closeForm}
        projectId={currentProject.id}
        editingLocation={state.editingLocation}
      />

      <BulkImportDialog
        open={state.isBulkImportOpen}
        onClose={handlers.closeBulkImport}
        onSuccess={handlers.handleImportSuccess}
        entityType="locations"
        projectId={currentProject.id}
      />

      <BulkExportDialog
        open={state.isBulkExportOpen}
        onClose={handlers.closeBulkExport}
        entityType="locations"
        data={getSafeLocations()}
        projectName={currentProject.name}
      />
    </ProfessionalLayout>
  );
};
